import React, { useContext, useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AssessmentSessionContext from '../../context/AssessmentSessionContext';
import { getDriveInfo } from '../../api/publicAssessApi';
import { FaClock, FaListOl, FaExclamationTriangle, FaCheckSquare, FaLaptop } from 'react-icons/fa';

const InstructionsPage = () => {
  const { token } = useParams();
  const { initSession, startSession } = useContext(AssessmentSessionContext);
  const navigate = useNavigate();
  const [agreed, setAgreed] = useState(false);
  const [driveInfo, setDriveInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const studentToken = localStorage.getItem(`studentToken:${token}`) || localStorage.getItem('studentToken');
    if (!studentToken) {
      navigate(`/register/${token}`, { replace: true });
      return;
    }

    const fetchDrive = async () => {
      try {
        const response = await getDriveInfo(token);
        setDriveInfo(response.data);
        initSession({
          token,
          driveInfo: response.data,
          questions: response.data.questions || [],
        });
        setError(null);
      } catch (err) {
        setError('Invalid or expired assessment link. Please check the URL.');
      } finally {
        setLoading(false);
      }
    };
    if (token) fetchDrive();
  }, [token, initSession]);

  const handleStart = () => {
    if (!agreed || !driveInfo) return;
    
    startSession(driveInfo.durationMinutes * 60);
    navigate(`/assess/${token}/test`, { replace: true });
  };

  if (loading) {
    return <div className="d-flex justify-content-center align-items-center vh-100"><div className="spinner-border text-primary-custom"></div></div>;
  }

  if (error || !driveInfo) {
    return <div className="container py-5 text-center"><div className="alert alert-danger p-4">{error}</div></div>;
  }

  return (
    <div className="container py-3 py-md-5 px-2 px-md-3">
      <div className="card custom-card mx-auto shadow-lg border-0 rounded-4" style={{ maxWidth: '850px' }}>
        <div className="card-body p-3 p-md-5">
          <h4 className="fw-bold mb-3 mb-md-4 pb-2 pb-md-3 border-bottom text-center text-primary-custom fs-4 fs-md-3">Important Instructions</h4>
          <ul className="list-group list-group-flush mb-4 mb-md-5">
            <li className="list-group-item bg-transparent px-0 py-2 py-md-3 d-flex gap-2 gap-md-3 border-light">
              <span className="text-primary-custom fw-bold">1.</span>
              <span>This assessment is strictly timed. The timer will start as soon as you click "Start Assessment" and cannot be paused.</span>
            </li>
            <li className="list-group-item bg-transparent px-0 py-2 py-md-3 d-flex gap-2 gap-md-3 border-light">
              <span className="text-primary-custom fw-bold">2.</span>
              <span>You can use the "Question Palette" to navigate directly to any question.</span>
            </li>
            <li className="list-group-item bg-transparent px-0 py-2 py-md-3 d-flex gap-2 gap-md-3 border-light text-danger bg-danger bg-opacity-10 rounded my-2 px-3 border border-danger">
              <FaLaptop size={24} className="mt-1 flex-shrink-0" />
              <span>
                <strong>Browser Violations:</strong> Do not switch tabs or minimize the browser window. Doing so will result in a violation. 
                After {driveInfo.maxViolations || 3} violations, your assessment will be automatically submitted and flagged.
              </span>
            </li>
            <li className="list-group-item bg-transparent px-0 py-2 py-md-3 d-flex gap-2 gap-md-3 border-light">
              <span className="text-primary-custom fw-bold">4.</span>
              <span>Ensure you have a stable internet connection before beginning. If you get disconnected, your progress is auto-saved and you can resume using the same link.</span>
            </li>
            {driveInfo.additionalInstructions && (
              <li className="list-group-item bg-transparent px-0 py-2 py-md-3 d-flex gap-2 gap-md-3 border-light">
                <span className="text-primary-custom fw-bold">5.</span>
                <span>{driveInfo.additionalInstructions}</span>
              </li>
            )}
          </ul>

          <div className="bg-light p-3 p-md-4 rounded-3 border">
            <div className="form-check">
              <input 
                className="form-check-input fs-5 mt-0" 
                type="checkbox" 
                id="agreeCheck" 
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
              />
              <label className="form-check-label fw-bold ms-2 cursor-pointer text-dark" htmlFor="agreeCheck">
                I have read and understood all the instructions and agree to abide by the academic integrity guidelines.
              </label>
            </div>
          </div>

          <div className="text-center mt-4 mt-md-5">
            <button 
              className="btn btn-lg btn-primary-custom px-4 px-md-5 rounded-pill shadow-sm fw-bold w-100 w-sm-auto" 
              onClick={handleStart}
              disabled={!agreed}
            >
              Start Assessment Now
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InstructionsPage;
